import React, { useMemo, useState } from "react";
import { Alert, Button, Empty, Select, Space, Spin, Tag } from "antd";
import {
  getPermissionIdByName,
  humanizeKey,
  splitPermissionName,
} from "./rolesPermissionsUtils";

const renderPermission = (name, type, onRemove) => {
  const { moduleKey, actionKey } = splitPermissionName(name);
  return (
    <div className="rp-effective-row" key={`${type}-${name}`}>
      <div>
        <strong>{humanizeKey(moduleKey)}</strong>
        <span>{humanizeKey(actionKey)}</span>
        <small>{name}</small>
      </div>
      <Space>
        <Tag color={type === "EXTRA" ? "geekblue" : "default"}>{type}</Tag>
        {type === "EXTRA" && onRemove && (
          <Button size="small" danger onClick={() => onRemove(name)}>
            Quitar
          </Button>
        )}
      </Space>
    </div>
  );
};

export default function UserEffectivePermissions({
  user,
  data,
  catalog,
  loading,
  error,
  canAssignPermissions,
  grantLoading,
  revokeLoadingName,
  onGrant,
  onRevoke,
}) {
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);
  const idByName = useMemo(() => getPermissionIdByName(catalog), [catalog]);

  const rolePermissions = useMemo(() => data?.role_permissions || [], [data]);
  const extraPermissions = useMemo(() => data?.extra_permissions || [], [data]);
  const effectivePermissions = useMemo(() => data?.effective_permissions || [], [data]);
  const effectiveSet = useMemo(() => new Set(effectivePermissions), [effectivePermissions]);

  const grantOptions = catalog
    .filter((permission) => !effectiveSet.has(permission.nombre))
    .map((permission) => ({
      value: permission.idpermiso,
      label: permission.nombre,
    }));

  const grant = async () => {
    if (!selectedPermissionId) return;
    await onGrant(selectedPermissionId);
    setSelectedPermissionId(null);
  };

  if (!user) {
    return <Empty description="Seleccione un usuario" />;
  }

  if (loading) {
    return (
      <div className="rp-centered">
        <Spin />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  if (!data) {
    return <Empty description="Usuario no disponible o fuera de su alcance." />;
  }

  return (
    <div className="rp-effective">
      <div className="rp-effective-header">
        <div>
          <h6>Usuario #{user.iduser}</h6>
          <span>Rol: {data.role?.name || "Sin rol activo"}</span>
        </div>
      </div>

      {canAssignPermissions && (
        <div className="rp-grant-row">
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Agregar permiso"
            value={selectedPermissionId}
            onChange={setSelectedPermissionId}
            options={grantOptions}
            style={{ minWidth: 260, flex: 1 }}
          />
          <Button type="primary" loading={grantLoading} disabled={!selectedPermissionId} onClick={grant}>
            Agregar permiso
          </Button>
        </div>
      )}

      <div className="rp-effective-columns">
        <section>
          <h6>Permisos del rol</h6>
          {rolePermissions.length ? (
            rolePermissions.map((name) => renderPermission(name, "ROL"))
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin permisos del rol" />
          )}
        </section>

        <section>
          <h6>Permisos adicionales</h6>
          {extraPermissions.length ? (
            extraPermissions.map((name) =>
              renderPermission(name, "EXTRA", async (permissionName) => {
                const idpermiso = idByName[permissionName];
                if (!idpermiso) return;
                await onRevoke(idpermiso, permissionName);
              })
            )
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin permisos extra" />
          )}
          {revokeLoadingName && <span className="rp-muted">Quitando {revokeLoadingName}...</span>}
        </section>

        <section>
          <h6>Permisos efectivos</h6>
          {effectivePermissions.length ? (
            effectivePermissions.map((name) => {
              const type = extraPermissions.includes(name) ? "EXTRA" : "ROL";
              return renderPermission(name, type);
            })
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin permisos efectivos" />
          )}
        </section>
      </div>
    </div>
  );
}
